import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  postBasicResultReport,
  postRPUnitReport,
  postMedicationReport,
  postRPIndexReport,
  postMedResultShortage,
} from '../../../../apis/reportPresc'
import { resetPrescriptionsCache } from '../../../../shared'
import { BasicResult, RPUnit, Medication } from '../../../../types/models/Presc'

// ============================================================
// 型定義
// ============================================================

type ReportBasicResultParams = {
  prescId: string
  key: keyof BasicResult
  isReported: boolean
}

type ReportRPUnitParams = {
  prescId: string
  rpNum: number
  key: keyof RPUnit
  isReported: boolean
}

type ReportMedicationParams = {
  prescId: string
  rpNum: number
  rpNumSub: number
  key: keyof Medication
  isReported: boolean
}

type ReportRPIndexParams = {
  prescId: string
  rpNum: number
  isReported: boolean
}

type ReportSpecificErrorParams = {
  prescId: string
  ocrErrorLabel: string
}

// ============================================================
// Mutation Hooks
// ============================================================

/**
 * 基本結果の報告 Mutation
 */
export const useReportBasicResultMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prescId, key, isReported }: ReportBasicResultParams) =>
      postBasicResultReport({ prescId, key, isReported }),
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * RP単位の報告 Mutation
 */
export const useReportRPUnitMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      prescId,
      rpNum,
      key,
      isReported,
    }: ReportRPUnitParams) =>
      postRPUnitReport({ prescId, rpNum, key, isReported }),
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * 薬剤の報告 Mutation
 */
export const useReportMedicationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      prescId,
      rpNum,
      rpNumSub,
      key,
      isReported,
    }: ReportMedicationParams) =>
      postMedicationReport({ prescId, rpNum, rpNumSub, key, isReported }),
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * RPインデックスの報告 Mutation
 */
export const useReportRPIndexMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prescId, rpNum, isReported }: ReportRPIndexParams) =>
      postRPIndexReport({ prescId, rpNum, isReported }),
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}

/**
 * 特定エラーの報告 Mutation（薬剤欠損等）
 */
export const useReportSpecificErrorMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prescId, ocrErrorLabel }: ReportSpecificErrorParams) =>
      postMedResultShortage({ prescId, ocrErrorLabel }),
    onSuccess: () => resetPrescriptionsCache(queryClient),
  })
}
